import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import isEmpty from 'lodash/isEmpty';

import useBaseReadServiceHook from '@hooks/services/useBaseReadServiceHook';
import {
  extractModalityLabelsFromFormats,
  extractPostingOptionsFromPostFormats,
  getConferenceBridgeText,
  getInternalConferenceBridgeFormFields,
} from '@redux/modules/communicator/utils';
import {convertCurlyBraceVariablesInFormFields} from '@redux/modules/communicator/variables-converter.utils';
import {getSessionOrgId} from '@redux/modules/session/utils';

import {
  COMBINED,
  CONFERENCE_BRIDGE,
  CONFIG,
  DELIVERY_PRIORITY_RULE,
  DELIVERY_RULES,
  EMAIL_MESSAGE_FIELDS,
  FORMATS,
  INTERNAL_CONFERENCE_BRIDGE_FIELD,
  MESSAGE_MODALITIES,
  MESSAGE_TEMPLATE_FIELDS,
  MESSAGE_TYPE_FIELD,
  MESSAGE_TYPE_VALUES,
  QUOTA_CONFIGURATION,
  QUOTA_CONFIGURATION_FIELD,
  QUOTA_REQUIRED_RESPONSES_FIELD,
  RECIPIENT_TYPE_ID_KEYS,
  SENDER_RECIPIENT_OPTIONS,
  SURVEY,
} from '@constants/communicator';

import communicatorApi from '@services/api/communicator';

import {deliveryPriorityRuleToRankingItems} from '@utils/deliveryMethodUtils/deliveryMethodUtils';

function useMessageTemplate(templateId) {
  const sessionOrgId = useSelector((state) => getSessionOrgId(state));

  const config = useMemo(() => {
    if (!isEmpty(templateId)) {
      return {
        apiCall: communicatorApi.getCustomMessageTemplate,
        pathParams: {organizationId: sessionOrgId, templateId: templateId},
      };
    }
  }, [sessionOrgId, templateId]);

  return useBaseReadServiceHook(config);
}

/**
 * Format the template that was returned by the service so that it can be used in our form. Several form items require changing
 * @param messageTemplate
 * @returns {*}
 */
export function formatMessageTemplateFromApi(messageTemplate) {
  if (!isEmpty(messageTemplate?.template)) {
    removeConferenceBridgeText(messageTemplate);

    const deliveryPriorityRule = messageTemplate.template.messageConfig?.[DELIVERY_RULES]?.[DELIVERY_PRIORITY_RULE];
    const deliveryRankingData = deliveryPriorityRule ? deliveryPriorityRuleToRankingItems(deliveryPriorityRule) : {};
    const quotaRule = messageTemplate.template.messageConfig?.[DELIVERY_RULES]?.quotaRule;
    const quotaManagedRecipients = quotaRule?.tiers?.[0]?.managedRecipients;
    const senderRecipientOptions =
      messageTemplate.template.senderRecipientOptions ||
      (quotaManagedRecipients
        ? {
            type: SENDER_RECIPIENT_OPTIONS.CAN_ADD_RECIPIENTS,
            requiredRecipients: quotaManagedRecipients,
          }
        : undefined);

    const quotaQuestion = messageTemplate.template.messageConfig?.[SURVEY]?.[QUOTA_CONFIGURATION_FIELD.QUESTIONS]?.[0];
    const quotaResponseOptions = quotaQuestion?.[QUOTA_CONFIGURATION_FIELD.RESPONSE_OPTIONS] || [];
    const quotaPositiveResponse = quotaResponseOptions.find(
      (option) => option?.[QUOTA_CONFIGURATION_FIELD.NUMBER] === 1,
    )?.text;
    const quotaNegativeResponse = quotaResponseOptions.find(
      (option) => option?.[QUOTA_CONFIGURATION_FIELD.NUMBER] === 2,
    )?.text;

    messageTemplate.template = {
      ...messageTemplate.template,
      ...messageTemplate.template.messageConfig,
      //Create a field messageTemplate.template.templateName with the same value as messageTemplate.template.name. Service inconsistent.
      [EMAIL_MESSAGE_FIELDS.templateName]: messageTemplate.template.name,
      //Subject and messageBody need to be populated at the root level for the form to pick them up.
      [EMAIL_MESSAGE_FIELDS.subject]: getSubject(messageTemplate),
      [EMAIL_MESSAGE_FIELDS.messageBody]: getBody(messageTemplate),
      //Create a field messageTemplate.template.messageModalities containing an array of all the modalities in the template.
      [EMAIL_MESSAGE_FIELDS.messageModalities]: getModalitiesInTemplate(messageTemplate),
      numRecipients: countRecipients({senderRecipientOptions}),
      [MESSAGE_TYPE_FIELD]: quotaRule ? MESSAGE_TYPE_VALUES.QUOTA : MESSAGE_TYPE_VALUES.MESSAGE,
      [MESSAGE_TEMPLATE_FIELDS.RECIPIENT_OPTION_SELECT]:
        senderRecipientOptions?.type || SENDER_RECIPIENT_OPTIONS.CAN_ADD_RECIPIENTS,
      ...(senderRecipientOptions ? {senderRecipientOptions} : {}),
      ...getInternalConferenceBridgeFormFields(
        messageTemplate.template.messageConfig[INTERNAL_CONFERENCE_BRIDGE_FIELD],
      ),
      ...extractModalityLabelsFromFormats(messageTemplate.template.messageConfig.formats),
      ...extractPostingOptionsFromPostFormats(messageTemplate.template.messageConfig?.postFormats),
      primaryContactInfoOnly: messageTemplate.template.primaryContactInfoOnly || false,
      ...(quotaRule && {
        [QUOTA_REQUIRED_RESPONSES_FIELD]: quotaRule.maxDesiredResponses,
        [QUOTA_CONFIGURATION]: {
          [QUOTA_CONFIGURATION_FIELD.QUESTION]: quotaQuestion?.[QUOTA_CONFIGURATION_FIELD.TEXT],
          [QUOTA_CONFIGURATION_FIELD.POSITIVE_RESPONSE]: quotaPositiveResponse,
          [QUOTA_CONFIGURATION_FIELD.NEGATIVE_RESPONSE]: quotaNegativeResponse,
        },
      }),
      ...(deliveryRankingData.rankingItems
        ? {
            [EMAIL_MESSAGE_FIELDS.deliveryRanking]: deliveryRankingData.rankingItems,
            [EMAIL_MESSAGE_FIELDS.deliveryWaitTime]: deliveryRankingData.waitTime,
          }
        : {
            // Explicitly set to null so form.setFieldsValue clears any previously loaded
            // template's deliveryRanking when switching to a template that uses org defaults.
            [EMAIL_MESSAGE_FIELDS.deliveryRanking]: null,
            [EMAIL_MESSAGE_FIELDS.deliveryWaitTime]: null,
          }),
    };

    messageTemplate.template = convertCurlyBraceVariablesInFormFields(messageTemplate.template);
  }

  return messageTemplate;
}

export function formatScheduledMessageFromApi(scheduledMessage) {
  if (!isEmpty(scheduledMessage?.config)) {
    removeConferenceBridgeText(scheduledMessage);

    const formatted = {
      ...scheduledMessage,
      ...scheduledMessage[CONFIG],
      combined: scheduledMessage[CONFIG][FORMATS][COMBINED],
      [EMAIL_MESSAGE_FIELDS.subject]: getSubject(scheduledMessage),
      [EMAIL_MESSAGE_FIELDS.messageBody]: getBody(scheduledMessage),
      template: {
        messageConfig: scheduledMessage[CONFIG],
      },
    };

    const modalities = getModalitiesInTemplate(scheduledMessage[CONFIG][FORMATS]);

    const deliveryPriorityRule = scheduledMessage[CONFIG]?.[DELIVERY_RULES]?.[DELIVERY_PRIORITY_RULE];
    const deliveryRankingData = deliveryPriorityRule ? deliveryPriorityRuleToRankingItems(deliveryPriorityRule) : {};

    const result = {
      ...formatted,
      [EMAIL_MESSAGE_FIELDS.messageModalities]: modalities,
      ...extractModalityLabelsFromFormats(scheduledMessage[CONFIG][FORMATS]),
      primaryContactInfoOnly: scheduledMessage.primaryContactInfoOnly || false,
      ...(deliveryRankingData.rankingItems
        ? {
            [EMAIL_MESSAGE_FIELDS.deliveryRanking]: deliveryRankingData.rankingItems,
            [EMAIL_MESSAGE_FIELDS.deliveryWaitTime]: deliveryRankingData.waitTime,
          }
        : {
            [EMAIL_MESSAGE_FIELDS.deliveryRanking]: null,
            [EMAIL_MESSAGE_FIELDS.deliveryWaitTime]: null,
          }),
      template: {
        ...formatted.template,
        [EMAIL_MESSAGE_FIELDS.messageModalities]: modalities,
      },
    };

    return convertCurlyBraceVariablesInFormFields(result);
  }

  return scheduledMessage;
}

/**
 * @param messageTemplate
 * @returns {string[]} An array of all the modalities which are used in messageTemplate
 */
function getModalitiesInTemplate(messageTemplate) {
  if (!isEmpty(messageTemplate?.template?.messageConfig?.formats)) {
    const arrayOfModalities = Object.keys(messageTemplate.template.messageConfig.formats).filter((modality) => {
      const arrayOfAllModalities = [
        MESSAGE_MODALITIES.EMAIL_FORMAT,
        MESSAGE_MODALITIES.PUSH_FORMAT,
        MESSAGE_MODALITIES.SMS_FORMAT,
        MESSAGE_MODALITIES.TTS_FORMAT,
        MESSAGE_MODALITIES.DESKTOP_FORMAT,
      ];
      //This will filter out anything in "formats" that is not a modality (include the "combined" field)
      const isAModality = arrayOfAllModalities.includes(modality);

      return isAModality;
    });

    return arrayOfModalities;
  }
}

export function countRecipients(data) {
  const requiredRecipients = data?.senderRecipientOptions?.requiredRecipients || {};
  const optionalRecipients = data?.senderRecipientOptions?.optionalRecipients || {};

  const keysWithPeople = Object.values(RECIPIENT_TYPE_ID_KEYS);

  const countFromRecipients = (recipients) => {
    let count = keysWithPeople.reduce((sum, key) => {
      const arr = recipients[key];
      return sum + (arr?.length || 0);
    }, 0);

    count += recipients.sites?.length || 0;

    return count;
  };

  return countFromRecipients(requiredRecipients) + countFromRecipients(optionalRecipients);
}

/**
 * Should remove the conference bridge text in the message body because this text is be re-appended
 * with the new conference bridge phone number on save
 * @param messageTemplate
 */
function removeConferenceBridgeText(messageTemplate) {
  const selectedModalities = getModalitiesInTemplate(messageTemplate);
  const conferenceBridge = messageTemplate?.template?.messageConfig?.[CONFERENCE_BRIDGE];

  if (!isEmpty(conferenceBridge)) {
    selectedModalities.forEach((selectedModality) => {
      const conferenceBridgeText = getConferenceBridgeText(selectedModality, conferenceBridge);
      const formats = messageTemplate.template.messageConfig[EMAIL_MESSAGE_FIELDS.formats];

      const indexOfConferenceBridgeText =
        formats[selectedModality][EMAIL_MESSAGE_FIELDS.messageBody].indexOf(conferenceBridgeText);
      if (indexOfConferenceBridgeText > 0) {
        formats[selectedModality][EMAIL_MESSAGE_FIELDS.messageBody] = formats[selectedModality][
          EMAIL_MESSAGE_FIELDS.messageBody
        ].slice(0, indexOfConferenceBridgeText);
      }
    });
  }
}

function getConfig(message) {
  return message?.template?.messageConfig ? message?.template?.messageConfig : message[CONFIG];
}

function getSubject(messageTemplate) {
  const config = getConfig(messageTemplate);

  const emailFormat = config?.[EMAIL_MESSAGE_FIELDS.formats]?.[MESSAGE_MODALITIES.EMAIL_FORMAT];
  const pushFormat = config?.[EMAIL_MESSAGE_FIELDS.formats]?.[MESSAGE_MODALITIES.PUSH_FORMAT];
  const desktopAlertFormat = config?.[EMAIL_MESSAGE_FIELDS.formats]?.[MESSAGE_MODALITIES.DESKTOP_FORMAT];

  return emailFormat
    ? emailFormat[EMAIL_MESSAGE_FIELDS.subject]
    : pushFormat
      ? pushFormat[EMAIL_MESSAGE_FIELDS.subject]
      : desktopAlertFormat
        ? desktopAlertFormat[EMAIL_MESSAGE_FIELDS.subject]
        : null;
}

function getBody(messageTemplate) {
  const config = getConfig(messageTemplate);
  const messageFormats = config?.[EMAIL_MESSAGE_FIELDS.formats];
  const emailFormat = messageFormats?.[MESSAGE_MODALITIES.EMAIL_FORMAT];
  const pushFormat = messageFormats?.[MESSAGE_MODALITIES.PUSH_FORMAT];
  const smsFormat = messageFormats?.[MESSAGE_MODALITIES.SMS_FORMAT];
  const ttsFormat = messageFormats?.[MESSAGE_MODALITIES.TTS_FORMAT];
  const desktopAlertFormat = messageFormats?.[MESSAGE_MODALITIES.DESKTOP_FORMAT];
  const messageBody = EMAIL_MESSAGE_FIELDS.messageBody;

  return (
    emailFormat?.[messageBody] ||
    pushFormat?.[messageBody] ||
    smsFormat?.[messageBody] ||
    ttsFormat?.[messageBody] ||
    desktopAlertFormat?.[messageBody]
  );
}

export default useMessageTemplate;
